import { type ReactNode, useId } from 'react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

/**
 * 전시·콘텐츠(ID44/ID45) 공통 드래그앤드롭 정렬 리스트.
 *
 * 기존 ↑↓ 핸들이 아닌 실제 마우스 드래그(+ 키보드 접근성)를 제공한다. 행 자체가 아닌
 * 좌측 그립 핸들(GripVertical)만 드래그 시작점으로 두어, 행 내부 버튼/링크 클릭과 충돌하지
 * 않게 한다. drop 시 새 순서 배열을 `onReorder` 로 올려 보내고, 호출부가 reorder API 를 호출한다.
 *
 * 낙관적 표시는 호출부 책임(여기서는 순수 표현). PointerSensor 의 8px 활성화 거리로
 * 의도치 않은 미세 드래그를 방지한다.
 */

export interface SortableListProps<T> {
  /** 현재 표시 순서의 항목 배열. */
  items: ReadonlyArray<T>;
  /** 각 항목의 안정적·고유 식별자(드래그 키). */
  getId: (item: T) => string | number;
  /** drop 으로 순서가 바뀌었을 때 호출 — 인자는 재정렬된 전체 배열. */
  onReorder: (reordered: T[]) => void;
  /**
   * 항목 1행 렌더(function-as-child). `dragHandle` 을 행 안에 배치해야 드래그가 시작된다.
   * children 으로 받아 제네릭 T 를 호출부 JSX 에서 자연스럽게 추론한다.
   */
  children: (item: T, index: number, dragHandle: ReactNode) => ReactNode;
  /** 드래그 비활성화(예: 진행 중 mutation). */
  disabled?: boolean;
  /** 드래그 핸들 접근성 라벨(스크린리더). */
  handleLabel?: string;
  className?: string;
}

interface SortableRowProps {
  id: string | number;
  disabled: boolean;
  handleLabel: string;
  children: (dragHandle: ReactNode) => ReactNode;
}

const SortableRow = ({ id, disabled, handleLabel, children }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };

  const handle = (
    <button
      type="button"
      aria-label={handleLabel}
      disabled={disabled}
      className="flex h-7 w-7 flex-shrink-0 cursor-grab touch-none items-center justify-center rounded transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)] disabled:cursor-not-allowed disabled:opacity-40 active:cursor-grabbing"
      style={{ color: 'var(--content-muted)' }}
      {...attributes}
      {...listeners}
    >
      <GripVertical size={16} aria-hidden="true" />
    </button>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {children(handle)}
    </div>
  );
};

export function SortableList<T>({
  items,
  getId,
  onReorder,
  children: renderItem,
  disabled = false,
  handleLabel = '순서 이동 핸들 (드래그하여 정렬)',
  className,
}: SortableListProps<T>) {
  // DndContext 는 동일 페이지에 여러 정렬 리스트가 있을 수 있어 고유 id 로 격리.
  const contextId = useId();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => getId(it) === active.id);
    const newIndex = items.findIndex((it) => getId(it) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove([...items], oldIndex, newIndex));
  };

  const ids = items.map(getId);

  return (
    <DndContext
      id={contextId}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item, index) => (
            <SortableRow
              key={getId(item)}
              id={getId(item)}
              disabled={disabled}
              handleLabel={handleLabel}
            >
              {(dragHandle) => renderItem(item, index, dragHandle)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
